"use client"

import { AnimatePresence, motion } from "framer-motion"
import { jsPDF } from "jspdf"
import { ArrowLeft, Check, Copy, Download, Facebook, Linkedin, Loader2, MessageSquare, Twitter } from "lucide-react"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { useAuth } from "../../context/AuthContext"
import Profile from "../assets/demo.jpg"
import { MemoizedMarkdown } from "../components/MemoizedMarkdown"
import { stripMarkdown } from "../components/utils/markdownUtils"
import Logo from "../assets/AI_District_Logo.png" // Declare the Logo variable

// Utility to remove hidden sections from markdown
function removeHiddenSections(markdown) {
  if (!markdown) return ""

  // Remove everything between <!-- HIDE_START --> and <!-- HIDE_END -->
  // Also remove the FINAL_STAGE:complete marker
  const processed = markdown
    .replace(/<!-- HIDE_START -->([\s\S]*?)<!-- HIDE_END -->/g, "")
    .replace(/<!-- FINAL_STAGE:complete -->/g, "")
    .replace(/^.*?[?].{50,}.*$/gm, "") // Remove questions with more than 50 words
    .replace(/^I see.*$/gm, "") // Remove "I see" lines
    .replace(/^Thank you.*$/gm, "") // Remove "Thank you" lines

  return processed
}

const ProfilePage = () => {
  const navigate = useNavigate()
  const { chatId } = useParams()
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState("summary")
  const [copied, setCopied] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [profileData, setProfileData] = useState(null)
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)
  const [showShareOptions, setShowShareOptions] = useState(false)
  const [notesAvailable, setNotesAvailable] = useState(true)
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    const fetchProfileData = async () => {
      setLoading(true)
      setError(null)

      try {
        // If we have a chatId, fetch that specific chat's data
        if (chatId) {

          const formattedData = {
            userName: user?.username || "User",
            email: user?.email || "user@example.com",
            bio: chatData.title || "Sandbox Agents",
            storyTitle: chatData.title || "Sandbox Agents",
            lastUpdated: new Date(chatData.updatedAt).toLocaleDateString(),
            summary: {
              title: chatData.title || "Sandbox Agents",
              content: chatData.notes || "No content available",
            },
            details: [],
          }


          setProfileData(formattedData)
        } else {
          // If no chatId, use default data
          setProfileData(defaultProfileData)
        }
      } catch (err) {
        console.error("Error fetching profile data:", err)
        // Instead of setting an error, use default data with a message
        const errorData = {
          ...defaultProfileData,
          summary: {
            ...defaultProfileData.summary,
            content: "Unable to load profile data. Please try again later.",
          },
        }
        setProfileData(errorData)
        setNotesAvailable(false)
      } finally {
        // Simulate a minimum loading time for better UX
        setTimeout(() => {
          setLoading(false)
        }, 1000)
      }
    }

    fetchProfileData()
  }, [chatId, user])

  useEffect(() => {
    if (profileData && profileData.details) {
      const totalQuestions = 10 // Adjust as needed
      const answered = profileData.details.length
      setProgress(Math.min(100, Math.round((answered / totalQuestions) * 100)))
    } else {
      setProgress(0)
    }
  }, [profileData]) // Update dependency to profileData

  const handleCopyToClipboard = () => {
    if (activeTab === "summary" && profileData?.summary?.content) {
      // Remove hidden sections and strip markdown before copying
      const visibleContent = removeHiddenSections(profileData.summary.content)
      const plainText = stripMarkdown(visibleContent)
      navigator.clipboard.writeText(plainText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } else if (activeTab === "details" && profileData?.details?.length > 0) {
      // Concatenate all details, remove hidden sections, and copy
      const detailsText = profileData.details
        .map(
          (item) =>
            `${stripMarkdown(item.section)}: ${item.question ? stripMarkdown(item.question) + "\n" : ""
            }${removeHiddenSections(item.answer)}`,
        )
        .join("\n\n")
      navigator.clipboard.writeText(detailsText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleDownloadPDF = () => {
    if (
      (activeTab === "summary" && (!profileData?.summary?.content || !notesAvailable)) ||
      (activeTab === "details" && (!profileData?.details || profileData.details.length === 0))
    )
      return

    try {
      setIsGeneratingPDF(true)

      const doc = new jsPDF({
        unit: "pt",
        format: "a4",
        putOnlyUsedFonts: true,
      })

      const PAGE = {
        width: 595.28,
        height: 841.89,
        marginX: 40,
        marginY: 60,
      }

      // Add a cover page
      doc.setFillColor(255, 255, 255) // White background
      doc.rect(0, 0, PAGE.width, PAGE.height, "F")

      // Add logo (try to use the app logo if available)
      try {
        doc.addImage(Logo, "JPEG", PAGE.width / 2 - 50, 100, 100, 100)
      } catch (e) {
        console.log("Could not add logo image")
      }

      // Add title
      doc.setTextColor(0, 0, 0)
      doc.setFontSize(20)
      doc.setFont("helvetica", "bold")
      const title = stripMarkdown(activeTab === "summary" ? profileData.summary.title : profileData.storyTitle)
      const wrappedTitle = doc.splitTextToSize(title, PAGE.width - PAGE.marginX * 2)
      doc.text(wrappedTitle, PAGE.width / 2, 250, { align: "center" })
      // Add some space after the title
      doc.setFontSize(12)
      doc.text("", PAGE.width / 2, 280, { align: "center" })

      // Add subtitle
      doc.setFontSize(16)
      doc.setFont("helvetica", "normal")
      doc.text("Created with Digital Product Concepting", PAGE.width / 2, 310, {
        align: "center",
      })

      // Add date
      const today = new Date()
      doc.setFontSize(12)
      doc.text(`Generated on ${today.toLocaleDateString()}`, PAGE.width / 2, 330, { align: "center" })

      // Add a new page for content
      doc.addPage()

      let y = PAGE.marginY
      const maxWidth = PAGE.width - PAGE.marginX * 2

      const processLine = (text, { fontSize, isBold, isBullet, color }) => {
        const lineHeight = fontSize * 1.5
        doc.setFontSize(fontSize).setFont("helvetica", isBold ? "bold" : "normal")
        doc.setTextColor(0, 0, 0)
        const cleanText = text.replace(/^\s*[#-*]+\s*/, "").replace(/\*\*/g, "")
        const indent = isBullet ? 15 : 0
        const wrappedText = doc.splitTextToSize(cleanText, maxWidth - indent)
        const spaceNeeded = wrappedText.length * lineHeight
        if (y + spaceNeeded > PAGE.height - PAGE.marginY) {
          doc.addPage()
          y = PAGE.marginY
        }
        if (isBullet && wrappedText.length > 0) {
          doc.text("•", PAGE.marginX, y + 2)
          wrappedText[0] = wrappedText[0].replace(/^•\s*/, "")
        }
        doc.text(wrappedText, PAGE.marginX + (isBullet ? 10 : 0), y, {
          indent: indent,
          maxWidth: maxWidth - indent,
        })
        y += spaceNeeded + (isBullet ? 5 : 8)
      }

      if (activeTab === "summary") {
        // Process the summary content, removing hidden sections
        const content = removeHiddenSections(profileData.summary.content)
        content.split("\n").forEach((line) => {
          const trimmed = line.trim()
          if (!trimmed) {
            y += 12
            return
          }
          if (trimmed.startsWith("# ")) {
            processLine(trimmed, { fontSize: 22, isBold: true, color: "#3b82f6" }) // Blue
          } else if (trimmed.startsWith("## ")) {
            processLine(trimmed, { fontSize: 18, isBold: true, color: "#10b981" }) // Green
          } else if (trimmed.startsWith("### ")) {
            processLine(trimmed, { fontSize: 16, isBold: true, color: "#f59e42" }) // Orange
          } else if (trimmed.match(/^[-*]\s/)) {
            processLine(trimmed, { fontSize: 12, isBullet: true, color: "#10b981" }) // Green bullets
          } else {
            processLine(trimmed, { fontSize: 12, color: "#374151" }) // Default dark gray
          }
        })
      } else if (activeTab === "details") {
        // Process the details content, removing hidden sections
        profileData.details.forEach((item) => {
          const sectionTitle = `${stripMarkdown(item.section)}: ${item.question ? stripMarkdown(item.question) : ""}`
          processLine(sectionTitle, { fontSize: 16, isBold: true, color: "#3b82f6" }) // Blue
          const answer = removeHiddenSections(item.answer)
          answer.split("\n").forEach((line) => {
            const trimmed = line.trim()
            if (!trimmed) {
              y += 12
              return
            }
            if (trimmed.startsWith("# ")) {
              processLine(trimmed, {
                fontSize: 16,
                isBold: true,
                color: "#FF6B6B",
              }) // Coral red
            } else if (trimmed.startsWith("## ")) {
              processLine(trimmed, {
                fontSize: 14,
                isBold: true,
                color: "#4ECDC4",
              }) // Turquoise
            } else if (trimmed.startsWith("### ")) {
              processLine(trimmed, {
                fontSize: 12,
                isBold: true,
                color: "#45B7D1",
              }) // Sky blue
            } else if (trimmed.match(/^[-*]\s/)) {
              processLine(trimmed, {
                fontSize: 12,
                isBullet: true,
                color: "#96CEB4",
              }) // Sage green
            } else {
              processLine(trimmed, { fontSize: 12, color: "#2C3E50" }) // Dark blue-gray
            }
          })
          y += 10
        })
      }

      // Save the PDF with the Digital Product Concepting title
      const fileName = `${stripMarkdown(
        activeTab === "summary" ? profileData.summary.title || "Sandbox Agents" : profileData.storyTitle || "Sandbox Agent Details",
      )}.pdf`
      doc.save(fileName)
      setTimeout(() => {
        toast.success("PDF downloaded successfully")
      }, 500)
    } catch (error) {
      console.error("Error generating PDF:", error)
      toast.error("Could not generate PDF. Please try again later.")
    } finally {
      setIsGeneratingPDF(false)
    }
  }

  const handleShareToSocial = (platform) => {
    const title = stripMarkdown(profileData?.summary?.title || "My Digital Product Concepting")
    const url = window.location.href
    let shareUrl = ""

    switch (platform) {
      case "twitter":
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`
        break
      case "facebook":
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          url,
        )}&quote=${encodeURIComponent(title)}`
        break
      case "linkedin":
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          url,
        )}&title=${encodeURIComponent(title)}`
        break
      default:
        return
    }

    window.open(shareUrl, "_blank", "width=600,height=400")
    setShowShareOptions(false)
  }

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
      },
    },
  }

  // Default profile data as fallback
  const defaultProfileData = {
    userName: user?.username || "User",
    email: user?.email || "user@example.com",
    bio: "No bio available",
    storyTitle: "Sandbox Agents",
    lastUpdated: new Date().toLocaleDateString(),
    summary: {
      title: "Sandbox Agents",
      content: "No content available yet. Please select a chat with completed content.",
    },
    details: [],
  }

  if (loading) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-gray-50">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col items-center"
        >
          <div className="w-16 h-16 rounded-full bg-gradient-to-r from-primary to-secondary flex items-center justify-center">
            <Loader2 className="w-12 h-12 text-white animate-spin" />
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-4 text-gray-800"
          >
            Loading your profile...
          </motion.p>
        </motion.div>
      </div>
    )
  }

  return (
    <motion.div
      className="w-screen min-h-screen p-2 lg:p-4 backdrop-blur-md"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="max-w-5xl mx-auto">
        {/* Header with profile info */}
        <motion.div className="mb-4 lg:mb-6 p-2 lg:p-4" variants={itemVariants}>
          <div className="flex items-start justify-between flex-wrap gap-2">
            <motion.button
              className="flex items-center gap-2 text-white rounded-xl px-3 py-1.5 hover:bg-gray-100 shadow-sm"
              whileHover={{ x: -5 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate("/dashboard")}
              style={{ background: "var(--primary-gradient)" }}
            >
              <ArrowLeft size={14} />
              <span className="text-sm">Back</span>
            </motion.button>

            <div className="flex gap-2">
              <motion.button
                className="flex items-center gap-2 text-gray-700 bg-white rounded-xl px-3 py-1.5 hover:bg-gray-100 shadow-sm text-sm"
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleDownloadPDF}
                disabled={isGeneratingPDF || !notesAvailable}
              >
                {isGeneratingPDF ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Download size={14} />
                    <span>PDF</span>
                  </>
                )}
              </motion.button>

              <div className="relative">
                {/* <motion.button
                  className="flex items-center gap-1 text-gray-700 bg-white rounded-xl px-3 py-1.5 hover:bg-gray-100 shadow-sm text-sm"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowShareOptions(!showShareOptions)}
                  disabled={!notesAvailable}
                >
                  <Share2 size={14} />
                  <span>Share</span>
                </motion.button> */}

                <AnimatePresence>
                  {showShareOptions && (
                    <motion.div
                      className="absolute right-0 mt-2 p-2 bg-white rounded-xl border border-gray-200 shadow-lg z-10"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                    >
                      <div className="flex flex-col gap-2 min-w-[150px]">
                        <button
                          className="flex items-center gap-2 text-gray-700 hover:bg-gray-100 p-2 rounded-lg text-sm"
                          onClick={() => handleShareToSocial("twitter")}
                        >
                          <Twitter size={16} />
                          <span>Twitter</span>
                        </button>
                        <button
                          className="flex items-center gap-2 text-gray-700 hover:bg-gray-100 p-2 rounded-lg text-sm"
                          onClick={() => handleShareToSocial("facebook")}
                        >
                          <Facebook size={16} />
                          <span>Facebook</span>
                        </button>
                        <button
                          className="flex items-center gap-2 text-gray-700 hover:bg-gray-100 p-2 rounded-lg text-sm"
                          onClick={() => handleShareToSocial("linkedin")}
                        >
                          <Linkedin size={16} />
                          <span>LinkedIn</span>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Profile Card */}
        <motion.div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 px-2 lg:px-4" variants={itemVariants}>
          {/* Left column - User info */}
          <motion.div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-md" variants={itemVariants}>
            <a href="https://aidistrictagents.com/profile" className="flex flex-col items-center">
              <motion.div
                className="relative"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, type: "spring" }}
              >
                <img
                  src={user?.profilePic ? `https://api.testir.xyz/server3${user?.profilePic}` : Profile}
                  alt="User Avatar"
                  className="w-32 h-32 rounded-full object-cover border-4 border-secondary shadow-lg"
                />
                <div className="absolute -bottom-2 -right-2 bg-primary rounded-full p-2 border-2 border-white">
                  <Check className="text-white size-4" />
                </div>
              </motion.div>

              <motion.h2 className="mt-4 text-2xl font-bold text-gray-800" variants={itemVariants}>
                {profileData.userName}
              </motion.h2>

              {/* <motion.div className="flex items-center gap-2 mt-1 text-gray-600" variants={itemVariants}>
                <Mail size={14} />
                <span className="text-sm">{profileData.email}</span>
              </motion.div> */}

              <motion.div
                className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-200 text-gray-700"
                variants={itemVariants}
              >
                <p className="text-sm">{stripMarkdown(profileData.bio)}</p>
              </motion.div>

              <motion.div className="mt-6 w-full" variants={itemVariants}>
                <div className="flex justify-between items-center text-gray-600 text-sm mb-2">
                  <span>Story Progress</span>
                  <span className="text-primary font-medium">{notesAvailable ? "100%" : "In Progress"}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  <motion.div
                    className="bg-gradient-to-r from-primary to-secondary h-full rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: notesAvailable ? "100%" : "70%" }}
                    transition={{ delay: 0.5, duration: 1 }}
                  />
                </div>
              </motion.div>

              <motion.div className="mt-6 w-full" variants={itemVariants}>
                <h3 className="text-gray-800 font-medium mb-2">Story Information</h3>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600 text-sm">Type:</span>
                    <span className="text-gray-800 text-sm font-medium">{stripMarkdown(profileData.storyTitle)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 text-sm">Last Updated:</span>
                    <span className="text-gray-800 text-sm font-medium">{profileData.lastUpdated}</span>
                  </div>
                </div>
              </motion.div>
            </a>
          </motion.div>

          {/* Right column - Story content */}
          <motion.div
            className="lg:col-span-2 bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-md"
            variants={itemVariants}
          >
            {/* Tabs */}
            <div className="flex border-b border-gray-200">
              <button
                className={`px-6 py-4 text-sm font-medium relative ${activeTab === "summary" ? "text-gray-800" : "text-gray-500 hover:text-gray-700"
                  }`}
                onClick={() => setActiveTab("summary")}
              >
                Summary
                {activeTab === "summary" && (
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary to-secondary"
                    layoutId="activeTab"
                  />
                )}
              </button>
              <button
                className={`px-6 py-4 text-sm font-medium relative ${activeTab === "details" ? "text-gray-800" : "text-gray-500 hover:text-gray-700"
                  }`}
                onClick={() => setActiveTab("details")}
              >
                Details
                {activeTab === "details" && (
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary to-secondary"
                    layoutId="activeTab"
                  />
                )}
              </button>
            </div>
            {/* Content */}
            <div className="p-6">
              {activeTab === "summary" ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-gray-800 text-xl font-bold">
                      {stripMarkdown(profileData.summary.title)} Summary
                    </h3>
                    {notesAvailable && (
                      <motion.button
                        className="flex items-center gap-1 text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg px-3 py-1.5 text-sm cursor-pointer"
                        onClick={handleCopyToClipboard}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {copied ? <Check size={14} /> : <Copy size={14} />}
                        <span>{copied ? "Copied!" : "Copy"}</span>
                      </motion.button>
                    )}
                  </div>

                  <div
                    className="bg-gray-50 rounded-xl p-6 border border-gray-200 text-gray-700 max-h-[500px] overflow-y-auto custom-scrollbar"
                    id="profile-content"
                  >
                    {!notesAvailable ? (
                      <div className="flex flex-col items-center justify-center py-8 text-center">
                        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                          <MessageSquare className="w-8 h-8 text-primary" />
                        </div>
                        <h4 className="text-gray-800 text-lg font-medium mb-2">Final Output Not Available Yet</h4>
                        <p className="text-gray-600 mb-4 max-w-md">
                          Continue your conversation to complete your Digital Product Concepting. We will generate your
                          final output once you've provided all the necessary information.
                        </p>
                        <motion.button
                          className="flex items-center gap-2 text-white bg-gradient-to-r from-primary to-secondary px-4 py-2 rounded-xl"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => navigate(`/dashboard`)}
                        >
                          <MessageSquare size={18} />
                          <span>Continue Conversation</span>
                        </motion.button>
                      </div>
                    ) : (
                      <div className="prose prose-gray prose-sm">
                        {typeof profileData.summary.content === "string" ? (
                          <MemoizedMarkdown
                            id="summary-content"
                            content={removeHiddenSections(profileData.summary.content)}
                            variant="profile"
                          />
                        ) : (
                          <p className="mb-4 text-gray-700 leading-relaxed">No summary content available.</p>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.3 }}
                  className="max-h-[500px] overflow-y-auto custom-scrollbar"
                >
                  <h3 className="text-gray-800 text-xl font-bold mb-4">Detailed Responses</h3>

                  <div className="space-y-4">
                    {profileData.details && profileData.details.length > 0 ? (
                      profileData.details.map((item) => (
                        <motion.div
                          key={item.section}
                          className="bg-gray-50 rounded-xl p-5 border border-gray-200"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1 }}
                        >
                          <h4 className="text-primary font-medium mb-2">
                            {stripMarkdown(item.section)}: {item.question ? stripMarkdown(item.question) : ""}
                          </h4>
                          <div className="text-gray-700 text-sm leading-relaxed mb-2">
                            <MemoizedMarkdown
                              id={`detail`}
                              content={removeHiddenSections(item.answer)}
                              variant="profile"
                            />
                          </div>
                        </motion.div>
                      ))
                    ) : (
                      <div className="bg-gray-50 rounded-xl p-5 border border-gray-200 text-center">
                        <p className="text-gray-500">No detailed responses available.</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  )
}

export default ProfilePage
